import EmailIcon from '@mui/icons-material/Email';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useDialogs } from '@toolpad/core/useDialogs';

import {
  getStudentQueryKey,
  resendOnboardingEmailMutation,
  sendOnboardingEmailMutation,
} from '@drivingdoc/api-client/generated/react-query';

import FeedbackDialog from 'components/Dialogs/FeedbackDialog';

import { useMenuControl } from 'hooks/useMenuControl';

import { getApiErrorMessage } from 'utils/getApiErrorMessage';

interface StudentProfileMenuProps {
  userId: string;
  needsOnboarding?: boolean;
  onboardingSentAt?: string | null;
  activatedAt?: string | null;
}

export function StudentProfileMenu({
  userId,
  needsOnboarding,
  onboardingSentAt,
  activatedAt,
}: StudentProfileMenuProps) {
  const studentProfileMenu = useMenuControl();
  const dialogs = useDialogs();
  const queryClient = useQueryClient();

  const sendOnboardingMutation = useMutation({
    ...sendOnboardingEmailMutation(),
    onSuccess: () => {
      // Invalidate the student query to refetch updated onboarding status
      queryClient.invalidateQueries({
        queryKey: getStudentQueryKey({ path: { id: userId } }),
      });
    },
  });

  const resendOnboardingMutation = useMutation({
    ...resendOnboardingEmailMutation(),
    onSuccess: () => {
      // Invalidate the student query to refetch updated onboarding status
      queryClient.invalidateQueries({
        queryKey: getStudentQueryKey({ path: { id: userId } }),
      });
    },
  });

  const handleSendOnboarding = async () => {
    studentProfileMenu.closeMenu();
    try {
      await sendOnboardingMutation.mutateAsync({ body: { userId } });
      dialogs.open(FeedbackDialog, {
        icon: 'success',
        title: 'Onboarding email sent successfully!',
      });
    } catch (err) {
      dialogs.open(FeedbackDialog, {
        title: 'Failed to send onboarding email',
        message: getApiErrorMessage(err),
      });
    }
  };

  const handleResendOnboarding = async () => {
    studentProfileMenu.closeMenu();
    try {
      await resendOnboardingMutation.mutateAsync({ body: { userId } });
      dialogs.open(FeedbackDialog, {
        icon: 'success',
        title: 'Onboarding email resent successfully!',
      });
    } catch (err) {
      dialogs.open(FeedbackDialog, {
        title: 'Failed to resend onboarding email',
        message: getApiErrorMessage(err),
      });
    }
  };

  // Determine if there are any available actions
  const showSendOnboardingOption = needsOnboarding && !activatedAt;
  const hasAvailableActions = showSendOnboardingOption;

  // Check if any onboarding mutation is in progress
  const isOnboardingPending = sendOnboardingMutation.isPending || resendOnboardingMutation.isPending;

  return (
    <>
      <Tooltip title="Student Actions">
        <IconButton aria-label="options" size="large" onClick={studentProfileMenu.openMenu}>
          <MoreVertIcon fontSize="inherit" />
        </IconButton>
      </Tooltip>

      <Menu
        {...studentProfileMenu.menuProps}
        onClick={studentProfileMenu.closeMenu}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        sx={{ mt: 1 }}
      >
        {/* Show send/resend onboarding option only if not activated */}
        {showSendOnboardingOption && (
          <MenuItem
            onClick={onboardingSentAt ? handleResendOnboarding : handleSendOnboarding}
            disabled={isOnboardingPending}
          >
            <ListItemIcon>
              <EmailIcon />
            </ListItemIcon>
            {onboardingSentAt ? 'Resend' : 'Send'} Onboarding Email
          </MenuItem>
        )}

        {/* Show empty state when no actions are available */}
        {!hasAvailableActions && (
          <MenuItem disabled>
            <ListItemIcon>
              <InfoOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="No actions available" primaryTypographyProps={{ color: 'text.secondary' }} />
          </MenuItem>
        )}

        {/* Add more menu items here in the future */}
      </Menu>

      {/* Backdrop loading indicator */}
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={isOnboardingPending}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </>
  );
}

export default StudentProfileMenu;
